/**
 * Central asset module: the single source of truth for Lightning Learn product
 * artwork on the website. Populated verbatim from LIGHTNING_LEARN_WEB_ASSETS.json
 * (manifest version 2026-08-31), captured from the live iOS app.
 *
 * RULES: use these exact URLs, alt text and copy. Never regenerate, redraw,
 * recolor, crop or reinterpret this art. Always render with object-contain on a
 * cream field (#F6EEDD). If an asset is missing, show fewer real assets. Never
 * invent a substitute.
 */

export interface AppImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface LessonCardAsset extends AppImage {
  label: string
  copy: string
}

export const welcomeHero: AppImage = {
  src:
    'https://d2ol7oe51mr4n9.cloudfront.net/user_3E0stOmz38DuKnNcE1JJF53oLaw/6922089e-f30d-4446-b72f-8445de3b8213.png',
  alt: 'Two curious kids and their fox buddy discovering worlds inside a glowing book',
  width: 1024,
  height: 1024,
}

/**
 * Featured homepage lesson: AA-03 “Why Do Octopuses Have Three Hearts?” from
 * Amazing Animals. Copy, alt text and artwork read straight from the app library
 * (lib/library/lessons-a.ts, lib/assets.ts, lib/library/collectibles.ts).
 */
export const featuredLesson = {
  lessonId: 'AA-03',
  world: 'Amazing Animals',
  title: 'Why Do Octopuses Have Three Hearts?',
  totalScreens: 15,
  collectible: {
    name: 'Octopus Explorer',
    alt: 'Octopus Explorer embroidered patch with a smiling purple octopus beneath three hearts.',
    src: 'https://cdn.magicpatterns.com/uploads/kUiDmhwfjG2hqCx45gzRzC/AA-03-embroidered-patch-v1.webp',
    width: 1024,
    height: 1024,
  } satisfies AppImage & { name: string },
  heroCard: {
    label: 'Card 1 of 15',
    copy: 'Why Do Octopuses Have Three Hearts?',
    alt:
      'An octopus with a cutaway view of its body showing two hearts beside the gills and one larger heart below them',
    src: 'https://cdn.magicpatterns.com/uploads/wXPDTrjrjud7mZwF2s5r1b/AA-03-octopus-three-hearts.png',
    width: 1024,
    height: 1024,
  } satisfies LessonCardAsset,
  answer:
    'Two hearts send blood to the gills to pick up oxygen. The third sends that oxygen-rich blood around the body.',
  tryIt:
    'Draw two feathery gills and one octopus body on paper. Place a small object beside each gill and one on the body, then trace the blood’s trip with your finger.',
  quickCheck: {
    question: 'Which hearts pump blood toward the gills?',
    reveal: 'The two smaller gill hearts, one beside each gill. The bigger body heart takes it from there.',
  },
  /** All twelve tap-through cards, in the app's authored order. */
  cards: [
    {
      label: 'Card 2 of 15',
      copy: 'An octopus already has eight arms, but hidden inside its soft body are three hearts.',
      alt: 'A friendly octopus spreads eight arms while three glowing heart shapes shine inside its mantle.',
      src: 'https://cdn.magicpatterns.com/uploads/nTpWYm76tackXUhEdgXsnc/AA-03-01-hook.png',
      width: 1024,
      height: 1024,
    },
    {
      label: 'Card 3 of 15',
      copy:
        'Why three? Its blood has to visit the gills for oxygen and then travel through the rest of the body.',
      alt:
        'A cutaway octopus has one blood path curving toward two feathery gills and another branching into all eight arms.',
      src: 'https://cdn.magicpatterns.com/uploads/6DwsDgxKKyKxfggQ3Zd9kK/AA-03-02-octopus-eight-arms-v2.webp',
      width: 960,
      height: 960,
    },
    {
      label: 'Card 4 of 15',
      copy: 'You might guess all three hearts do the same job. They do not.',
      alt:
        'A crossed-out thought bubble shows three identical hearts pushing the same way while a child examines an octopus circulation model.',
      src: 'https://cdn.magicpatterns.com/uploads/3LASfdtvx58gPvUr4QHcrt/AA-03-03-myth.png',
      width: 1024,
      height: 1024,
    },
    {
      label: 'Card 5 of 15',
      copy:
        'That would not move blood along the right route. The gills need blood coming in, while the body needs oxygen-rich blood going out.',
      alt:
        'Two panels compare three hearts pushing only toward the body with two small hearts pumping to the gills and one larger heart pumping outward.',
      src: 'https://cdn.magicpatterns.com/uploads/72a7ZZmdyeaB4J4P7fVX96/AA-03-04-myth-fails.png',
      width: 1024,
      height: 1024,
    },
    {
      label: 'Card 6 of 15',
      // App copy uses an em dash here; replaced with a comma per the website copy rule.
      copy: 'Two smaller hearts sit beside the octopus’s two gills, one heart for each gill.',
      alt:
        'A friendly cutaway octopus has two feathery gills inside its mantle, each with one small heart beside it.',
      src: 'https://cdn.magicpatterns.com/uploads/7UMGxiACPumAJ5o78jRhd6/AA-03-05-mechanism-1.png',
      width: 1024,
      height: 1024,
    },
    {
      label: 'Card 7 of 15',
      copy: 'These are the gill hearts. They push oxygen-poor blood toward the gills.',
      alt: 'Two small hearts squeeze dark teal blood dots toward the feathery gill beside each heart.',
      src: 'https://cdn.magicpatterns.com/uploads/9cSYGkQhAfMzHpEWrsG2f1/AA-03-06-mechanism-2.png',
      width: 1024,
      height: 1024,
    },
    {
      label: 'Card 8 of 15',
      copy: 'As water flows over the gills, oxygen moves into the blood.',
      alt: 'A magnified gill has water and oxygen bubbles flowing across it as dark blood dots become brighter.',
      src: 'https://cdn.magicpatterns.com/uploads/ewU67vB6twfdc8UsGLHKMa/AA-03-07-mechanism-3.png',
      width: 1024,
      height: 1024,
    },
    {
      label: 'Card 9 of 15',
      copy: 'The third, larger body heart receives that oxygen-rich blood and pumps it through the octopus.',
      alt: 'One large heart sends bright teal blood through branching paths into an octopus’s mantle and all eight arms.',
      src: 'https://cdn.magicpatterns.com/uploads/nEUDift3HVPe4wk4g7LrTn/AA-03-08-octopus-eight-arms-v2.webp',
      width: 960,
      height: 960,
    },
    {
      label: 'Card 10 of 15',
      copy:
        'Again and again, the route repeats: two hearts pump toward the gills before one heart pumps toward the body.',
      alt: 'Three stages show two hearts pumping to the gills, oxygen entering the blood, and one heart pumping to the body.',
      src: 'https://cdn.magicpatterns.com/uploads/6ELnaApZLV9aUdDycep9a2/AA-03-09-scale.png',
      width: 1024,
      height: 1024,
    },
    {
      label: 'Card 11 of 15',
      copy: 'Unlike our red blood, octopus blood looks blue. Its oxygen-carrying helper uses copper instead of iron.',
      alt:
        'Two panels compare a child beside a coral-red blood drop with an octopus beside a teal-violet blood drop filled with copper-colored dots.',
      src: 'https://cdn.magicpatterns.com/uploads/jtVmVMKAVhRcNUgP1NYkPa/AA-03-10-variation.png',
      width: 1024,
      height: 1024,
    },
    {
      label: 'Card 12 of 15',
      copy: 'Every one of the octopus’s eight busy arms depends on the oxygen delivered by that one body heart.',
      alt:
        'An octopus explores shells and rocks as eight bright circulation paths run from its body heart to the tips of its arms.',
      src: 'https://cdn.magicpatterns.com/uploads/sU7KKYVxLx23q9CwgM38vm/AA-03-11-human-connection.png',
      width: 1024,
      height: 1024,
    },
    {
      label: 'Card 13 of 15',
      copy:
        'Three hearts share the work: two gill hearts send blood to the gills for oxygen, and one body heart sends oxygen-rich blood everywhere else.',
      alt: 'An octopus is ringed by four badges showing gill hearts, gills, oxygen entering blood, and a body heart pumping to the arms.',
      src: 'https://cdn.magicpatterns.com/uploads/mW4nhRSkUvdxhuD8qJWzKs/AA-03-12-summary.png',
      width: 1024,
      height: 1024,
    },
  ] satisfies LessonCardAsset[],
}

export interface WorldAsset extends AppImage {
  name: string
}

export const worldAssets: WorldAsset[] = [
  {
    name: 'Amazing Animals',
    alt: 'A leaping fox, a flying bird and an octopus tentacle rising from a splash of water',
    src: 'https://cdn.magicpatterns.com/uploads/hfay8qyv9QQCU9ra28N8fk/CAT-01-amazing-animals.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Space & Beyond',
    alt: 'A bright ringed planet with a small rocket flying past it under a crescent moon and stars',
    src: 'https://cdn.magicpatterns.com/uploads/g1ayfU9674fCW7jtrf5Pfg/CAT-02-space-beyond.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Your Incredible Body',
    alt: 'A running child with a heart on their shirt, a brain sparking above them and a bone beside them',
    src: 'https://cdn.magicpatterns.com/uploads/hPCCbWMPELTmqw7VZJAsw9/CAT-03-incredible-body.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Our Wild Earth',
    alt: 'An erupting volcano beside blue mountains, a winding river and a small pine forest',
    src: 'https://cdn.magicpatterns.com/uploads/1EmUe6XW5AuEDN6V75EQvE/CAT-04-wild-earth.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'How Things Work',
    alt: 'Three linked gears turning together with arrows leading to a glowing light bulb',
    src: 'https://cdn.magicpatterns.com/uploads/wiwTDou6kgnDZnhWnjhuTv/CAT-05-how-things-work.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Long Ago',
    alt:
      'A trowel digging in layered soil beside a patterned clay pot, a spiral shell fossil and rolled scrolls',
    src: 'https://cdn.magicpatterns.com/uploads/cmmdzxQd34x1RAendiN4fP/CAT-06-long-ago.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Ocean Secrets',
    alt: 'A whale tail splashing at the surface above a reef of coral and a glowing jellyfish',
    src: 'https://cdn.magicpatterns.com/uploads/bQNCtEuo6AAgW37PwSL5UP/CAT-07-ocean-secrets.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Dinosaur Days',
    alt: 'A feathered dinosaur stepping into a fossil footprint with a long-necked dinosaur behind it',
    src: 'https://cdn.magicpatterns.com/uploads/goDiyKg91gYZqySeR6WtZX/CAT-08-dinosaur-days.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Tiny Worlds',
    alt: 'A microscope over a glowing water droplet, surrounded by a magnified cell, a crystal and a microbe',
    src: 'https://cdn.magicpatterns.com/uploads/5TjEbHk7Dtv9ebRPT6bxrJ/CAT-09-tiny-worlds.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Wild Weather',
    alt: 'A storm cloud with a swirl of wind, falling rain, a lightning bolt and the sun peeking out behind',
    src: 'https://cdn.magicpatterns.com/uploads/uK86fBgRDsBVVFASkrfoKx/CAT-10-wild-weather.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Food Adventures',
    alt:
      'A loaf of bread rising with bubbles around it, circled by ribbons, beside a strawberry and a wedge of cheese',
    src: 'https://cdn.magicpatterns.com/uploads/drCkVBT4KMriHQT63XgrwZ/CAT-11-food-adventures.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Machines on the Move',
    alt: 'A speeding train with an airplane flying above it and a spinning bicycle wheel beside it',
    src: 'https://cdn.magicpatterns.com/uploads/1ViK7ZhbNeypnEoSrgWNEd/CAT-12-machines-on-the-move.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Art, Sound & Imagination',
    alt: 'A paintbrush sweeping a rainbow ribbon across the frame with a guitar and a blob of mixed paint',
    src: 'https://cdn.magicpatterns.com/uploads/vRcjrh9xoa1R9zgNJsbRoM/CAT-13-art-sound-imagination.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Amazing Places & Cultures',
    alt: 'A round scene of a stone bridge over water with a sailboat, hillside houses and a market stall',
    src: 'https://cdn.magicpatterns.com/uploads/bcjhf7yAdZdXnnkWnyKbCx/CAT-14-amazing-places-cultures.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Extreme Worlds',
    alt: 'Four extreme places joined together: a polar bear on ice, a camel in hot dunes, and deep-sea vents below',
    src: 'https://cdn.magicpatterns.com/uploads/mTKzacvi4vGNNViYme1KWD/CAT-15-extreme-worlds.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Strange but True',
    alt: 'A straw looking bent in a glass of water, a floating hole-filled rock and a huge moon low on the horizon',
    src: 'https://cdn.magicpatterns.com/uploads/i7E56MJwVUJKNDf9UVeNR2/CAT-16-strange-but-true.png',
    width: 1024,
    height: 1024,
  },
]

/** Badges are earned one per completed lesson. No thresholds are attached. */
export interface CollectibleAsset extends AppImage {
  name: string
}

export const collectibleAssets: CollectibleAsset[] = [
  {
    name: 'Woodpecker Explorer',
    alt: 'Woodpecker Explorer embroidered patch with a red-crested woodpecker tapping a tree.',
    src: 'https://cdn.magicpatterns.com/uploads/7GXocHdx6jyX3zSo679kyB/AA-01-embroidered-patch-v1.webp',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Otter Guardian',
    alt: 'Otter Guardian embroidered patch with two sea otters holding paws beside kelp.',
    src: 'https://cdn.magicpatterns.com/uploads/bLdJmzf5DDUGrq6hV5Rbb7/AA-10-embroidered-patch-v1.webp',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Heartbeat Tracker',
    alt: 'Heartbeat Tracker embroidered patch with a coral heart with a cream heartbeat line.',
    src: 'https://cdn.magicpatterns.com/uploads/2VyKXsy7EbRpMm5xJ6wupz/BI-05-embroidered-patch-v1.webp',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Plant Signal Scout',
    alt: 'Plant Signal Scout embroidered patch showing two leafy plants with small airborne chemical signal dots and a branching soil-root network.',
    src: 'https://cdn.magicpatterns.com/uploads/d9G5U7Mv4YHckenVPDSwoc/ST-10-embroidered-patch-v1.webp',
    width: 1024,
    height: 1024,
  },
]

export interface BuddyAsset extends AppImage {
  name: string
}

export const buddyAssets: BuddyAsset[] = [
  {
    name: 'Fox',
    alt: 'Orange fox explorer with a teal shoulder bag',
    src: 'https://cdn.magicpatterns.com/uploads/h62wfJNhiNGa5aCdkhSUNk/buddy-fox.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Parrot',
    alt: 'Vivid red, yellow, and blue parrot wearing purple-and-teal headphones and flashing a peace sign',
    src:
      'https://d2ol7oe51mr4n9.cloudfront.net/user_3E0stOmz38DuKnNcE1JJF53oLaw/4c7668fc-f2e3-4556-9596-42dae7221dbe.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Dolphin',
    alt: 'Bright blue dolphin wearing orange sunglasses and giving a fin-up',
    src:
      'https://d2ol7oe51mr4n9.cloudfront.net/user_3E0stOmz38DuKnNcE1JJF53oLaw/cf17532c-6a50-472a-b330-2896d7f77245.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Robot',
    alt: 'Teal retro robot with a lightning bolt giving a thumbs-up',
    src: 'https://cdn.magicpatterns.com/uploads/8SWXCPPps6vygypYDGadoY/buddy-robot.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Tiger',
    alt: 'Orange tiger explorer wearing teal goggles',
    src: 'https://cdn.magicpatterns.com/uploads/unnu8Z3vDpVjN4iWUSZgPm/buddy-tiger.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Axolotl',
    alt: 'Aqua axolotl explorer holding a magnifying glass',
    src: 'https://cdn.magicpatterns.com/uploads/eNfbuqHSNKfXPDv84Y3ke6/buddy-axolotl.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Triceratops',
    alt: 'Teal triceratops adventurer wearing a navy bandana',
    src: 'https://cdn.magicpatterns.com/uploads/17LJCh1XFkYUpT2cvVfzjX/buddy-triceratops.png',
    width: 1024,
    height: 1024,
  },
  {
    name: 'Monkey',
    alt: 'Golden-brown monkey wearing a backward coral cap',
    src: 'https://cdn.magicpatterns.com/uploads/rLWZrwPTT9QyuY99jpBN4Q/buddy-monkey.png',
    width: 1024,
    height: 1024,
  },
]

export const foxBuddy: BuddyAsset = buddyAssets[0]
